import { DIALOG_DATA } from '@angular/cdk/dialog'
import { ScrollingModule } from '@angular/cdk/scrolling'

import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject, OnDestroy, OnInit, signal } from '@angular/core'
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import {
  PalantirButtonComponent, PalantirDialogComponent,
  PalantirDialogRef,
  PalantirIconComponent,
  PalantirInputComponent,
  PalantirListItemComponent, PalantirRadioComponent,
  PalantirSkeletonComponent,
  PalantirTextComponent
} from '@fieldcontrol/palantir'
import { WarrantyTemplate } from '@generated/sammathnaur.graphql'
import { TranslateModule } from '@ngx-translate/core'
import { NgScrollbarModule } from 'ngx-scrollbar'
import { WarrantiesService } from 'projects/fce-components/src/app/core/api/warranties/warranties.service'
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs'

@Component({
  selector: 'app-warranties-dialog',
  templateUrl: './warranties-dialog.component.html',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    PalantirTextComponent,
    PalantirListItemComponent,
    PalantirDialogComponent,
    PalantirButtonComponent,
    ScrollingModule,
    NgScrollbarModule,
    PalantirIconComponent,
    MatProgressSpinnerModule,
    PalantirInputComponent,
    TranslateModule,
    PalantirRadioComponent,
    PalantirSkeletonComponent
],
  schemas: [
    CUSTOM_ELEMENTS_SCHEMA
  ]
})
export class WarrantiesDialogComponent implements OnInit, OnDestroy {
  private readonly data = inject(DIALOG_DATA)
  private readonly palantirDialogRef = inject(PalantirDialogRef)
  public readonly warrantiesService = inject(WarrantiesService)
  private destroy$ = new Subject<void>()

  public warrantiesTemplates = signal<WarrantyTemplate[]>([])
  public selectedWarrantyTemplate = signal<WarrantyTemplate | null>(null)
  public progress = signal<number>(0)

  public searchControl = new FormControl('')
  public form!: FormGroup

  public isSelected = (id: string) => computed(() => this.selectedWarrantyTemplate()?.id === id)

  public async ngOnInit(): Promise<void> {
    this.form = new FormGroup({
      warrantyTemplateId: new FormControl(null, [Validators.required]),
      companyId: new FormControl(this.data.selectedCompanyId, [Validators.required]),
    })
    this.warrantiesService.filterForm.patchValue({ nameContains: null, type: 'maintenances', maintenanceTypeId: this.data.selectedMaintenanceTypeId })
    this.searchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(async () => {
      await this.search()
    })

    await this.search()
  }

  public ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  public async close(): Promise<void> {
    await this.palantirDialogRef.close()
  }

  private async search(): Promise<void> {
    this.warrantiesService.filterForm.patchValue({ nameContains: this.searchControl.value, type: 'maintenances', maintenanceTypeId: this.data.selectedMaintenanceTypeId })
    this.warrantiesService.isLoading.set(true)
    const result = await this.warrantiesService.searchPage()
    this.warrantiesTemplates.set((result.data?.warrantiesTemplates?.items ?? []) as WarrantyTemplate[])
    this.warrantiesService.isLoading.set(false)
  }

  public toggleWarrantyTemplate(warrantyTemplate: WarrantyTemplate): void {
    this.selectedWarrantyTemplate.set(warrantyTemplate)
    this.form.get('warrantyTemplateId')?.setValue(warrantyTemplate.id)
  }

  public async save(): Promise<void> {
    this.warrantiesService.isLoading.set(true)
    await this.palantirDialogRef.close({ warrantyTemplate: this.selectedWarrantyTemplate() })
  }
}
